import { usePageBackOptions } from '@/contexts/PageNavigationContext';
// ⚡ COCKPIT — single-screen redesign voor InkomendeDocumentDetailPage
// Doel: 3× sneller batch-verwerken van AI-geparsed inkomende facturen.
// - Links: grote PDF viewer (60%)
// - Rechts: compact panel met alle essentiële velden (40%)
// - Onderaan: "Opslaan & Volgende"-knop (Ctrl+Enter)
// - Voortgangs-balk bovenaan: "3 van 12 verwerkt"

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import {
  ArrowLeft, ArrowRight, CheckCircle, MagicWand, FloppyDisk, Lightning, CaretLeft, Eye, SkipForward,
} from '@phosphor-icons/react';

// Compacte vlakke lijst van categorieën — geen geneste dropdown nodig in cockpit
const CATEGORIE_PLAT = [
  'Elektriciteit (productie + kantoor)', 'Gas / verwarming', 'Waterverbruik', 'Brandverzekering',
  'Onderhoud machines', 'Herstellingen gebouw', 'Telefoon & internet abonnementen',
  'Softwarelicenties', 'Kantoorbenodigdheden', 'Drukwerk', 'Bankkosten', 'Boekhouder / accountant',
  'Marketing & reclame', 'Website / hosting', 'Opleidingen',
  'Machines', 'Gereedschappen', 'IT hardware',
  'Lonen arbeiders & bedienden', 'Interimkrachten', 'Werkkledij / PBM',
  'Transporteurs', 'Loonwerk', 'Montageploegen',
  'Grondstoffen (staal DX51D)', 'Halffabricaten', 'Inkoop profielen', 'Verpakkingsmateriaal',
  'Huur bedrijfspand', 'Huur magazijn', 'Huur voertuigen',
  'Brandstof wagen', 'Verzekering wagen', 'Onderhoud wagen',
  'Brandstof bestelwagen', 'Onderhoud bestelwagen', 'Tolkosten',
  'Verkoperscommissie', 'Consultancy fees', 'BA verzekering', 'Arbeidsongevallen',
  'RSZ bijdragen', 'Zelfstandigen bijdragen',
];

export default function InkomendeDocumentCockpitPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);
  const goBack = usePageBackOptions({ fallback: '/inkomende-documenten', dirty, busy: saving });
  const [aiParsing, setAiParsing] = useState(false);
  const [queue, setQueue] = useState([]); // alle te-verwerken documenten (voor "Volgende")
  const [queueIdx, setQueueIdx] = useState(-1);
  const timerRef = useRef(null);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : '';
  const bestandUrl = `${API_BASE}/api/inkomende-documenten/${id}/bestand`;

  const load = async () => {
    setLoading(true);
    try {
      const r = await API.get(`/inkomende-documenten/${id}`);
      setDoc(r.data);
      setForm(r.data);
    } catch {
      navigate('/inkomende-documenten');
    }
    setLoading(false);
  };

  // Wachtrij van te-verwerken documenten ophalen voor "Volgende"-functionaliteit
  const loadQueue = async () => {
    try {
      const r = await API.get('/inkomende-documenten');
      // Enkel 'te_verwerken' of 'verwerkt' (= nog niet goedgekeurd)
      const teDoen = (r.data || []).filter(d => ['te_verwerken', 'verwerkt'].includes(d.status));
      setQueue(teDoen);
      const idx = teDoen.findIndex(d => d.id === id);
      setQueueIdx(idx);
    } catch { /* niet kritisch */ }
  };

  useEffect(() => { load(); loadQueue(); /* eslint-disable-next-line */ }, [id]);

  const u = (field) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm(p => ({ ...p, [field]: v }));
    setDirty(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(autoSave, 600);
  };

  const autoSave = async () => {
    if (!form?.id) return;
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload._id; delete payload.user_id; delete payload.id;
      await API.put(`/inkomende-documenten/${id}`, payload);
      setDirty(false);
    } catch { /* silent fail */ }
    setSaving(false);
  };

  const runAI = async () => {
    setAiParsing(true);
    try {
      const r = await API.post(`/inkomende-documenten/${id}/ai-parse`);
      if (r.data?.data) {
        const ex = r.data.data;
        delete ex._regel_toegepast; delete ex._duplicaat;
        setForm(p => ({ ...p, ...ex }));
        setDirty(true);
        setTimeout(autoSave, 300);
      }
    } catch (e) { alert('AI parse mislukt: ' + (e.response?.data?.detail || 'fout')); }
    setAiParsing(false);
  };

  // "Opslaan & Volgende" — sla op, ga naar volgende document in wachtrij
  const opslaanEnVolgende = async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    await autoSave();
    // Goedkeuren als status nog 'te_verwerken' of 'verwerkt'
    try {
      if (form.status && ['te_verwerken', 'verwerkt'].includes(form.status)) {
        await API.post(`/inkomende-documenten/${id}/goedkeuren`);
      }
    } catch { /* niet kritisch */ }
    const next = queue.find((d, i) => i > queueIdx && d.id !== id);
    if (next) {
      navigate(`/inkomende-documenten/${next.id}/cockpit`);
    } else {
      alert('🎉 Alle inkomende documenten zijn verwerkt!');
      navigate('/inkomende-documenten');
    }
  };

  // ⏭️ Sla over — naar volgend zonder opslaan/goedkeuren (status blijft 'te_verwerken')
  const slaOver = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const next = queue.find((d, i) => i > queueIdx && d.id !== id);
    if (next) {
      navigate(`/inkomende-documenten/${next.id}/cockpit`);
    } else {
      alert('🎉 Geen volgend document. Alle overige documenten zijn verwerkt of overgeslagen.');
      navigate('/inkomende-documenten');
    }
  };

  // Keyboard-shortcuts:
  //   Ctrl+Enter        = Opslaan & Volgende
  //   Ctrl+Shift+Enter  = Sla over
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (e.shiftKey) slaOver();
        else opslaanEnVolgende();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line
  }, [form, queueIdx, queue]);

  if (loading || !doc) {
    return <div className="p-8 text-center text-gray-500">Bezig met laden...</div>;
  }

  const voortgangPct = queue.length > 0 ? Math.round(((queueIdx + 1) / queue.length) * 100) : 0;

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-gray-50" data-testid="cockpit-page">
      {/* ─── Voortgang-balk + acties ────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-3">
        <button type="button" onClick={() => goBack()} className="text-gray-500 hover:text-gray-800 inline-flex items-center gap-1 text-xs" data-testid="cockpit-back">
          <ArrowLeft size={14} /> Terug
        </button>
        <span className="text-gray-300">|</span>
        <div className="flex items-center gap-2 flex-1">
          <span className="text-xs font-bold text-gray-700 whitespace-nowrap">
            {queueIdx >= 0 ? `${queueIdx + 1} van ${queue.length}` : 'Document'}
          </span>
          <div className="flex-1 bg-gray-200 h-2 rounded-full overflow-hidden max-w-md">
            <div className="h-full bg-gradient-to-r from-[#002FA7] to-[#0EA5E9] transition-all" style={{ width: `${voortgangPct}%` }} />
          </div>
          <span className="text-xs text-gray-500">{voortgangPct}%</span>
        </div>
        <Link to={`/inkomende-documenten/${id}`} className="text-xs text-gray-500 hover:text-[#002FA7] inline-flex items-center gap-1" title="Klassieke weergave" data-testid="cockpit-classic-link">
          <Eye size={12} /> Klassieke weergave
        </Link>
        <span className="text-[10px] px-2 py-1 bg-[#FFF1E0] text-[#9A3412] font-bold uppercase tracking-wider border border-orange-200" title="Cockpit = nieuwe single-screen layout voor snelle batchverwerking">
          ⚡ COCKPIT (DEMO)
        </span>
      </div>

      {/* ─── Hoofdblok: PDF links | velden rechts ────────────────── */}
      <div className="flex-1 flex overflow-hidden">
        {/* PDF VIEWER — 60% breedte */}
        <div className="flex-1 bg-gray-900 relative" style={{ flexBasis: '60%' }} data-testid="cockpit-pdf">
          <iframe
            src={`${bestandUrl}?token=${encodeURIComponent(token || '')}`}
            className="w-full h-full"
            title="PDF preview"
            style={{ border: 'none' }}
          />
        </div>

        {/* COCKPIT PANEL — 40% breedte */}
        <div className="bg-white border-l border-gray-200 overflow-y-auto" style={{ flexBasis: '40%', minWidth: '480px' }} data-testid="cockpit-panel">
          <div className="p-4 space-y-3">
            {/* AI-knop bovenaan */}
            <button
              type="button"
              onClick={runAI}
              disabled={aiParsing}
              className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white font-bold py-2 hover:opacity-90 disabled:opacity-50"
              data-testid="cockpit-ai-btn"
            >
              <MagicWand size={16} weight="bold" /> {aiParsing ? 'AI bezig...' : 'AI herkenning (her)starten'}
            </button>

            {/* Leverancier */}
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-0.5">Leverancier</label>
                <input className="field-input w-full" value={form.leverancier || ''} onChange={u('leverancier')} data-testid="cockpit-leverancier" autoFocus />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-0.5">BTW-nummer</label>
                <input className="field-input w-full font-mono text-xs" value={form.btw_nummer || ''} onChange={u('btw_nummer')} data-testid="cockpit-btw-nummer" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-0.5">IBAN</label>
                <input className="field-input w-full font-mono text-xs" value={form.iban || ''} onChange={u('iban')} data-testid="cockpit-iban" />
              </div>
            </div>

            {/* Factuurinfo */}
            <div className="border-t border-gray-100 pt-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-0.5">Factuurnummer</label>
                  <input className="field-input w-full font-mono" value={form.factuurnummer || ''} onChange={u('factuurnummer')} data-testid="cockpit-factuurnummer" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-0.5">Type</label>
                  <select className="field-input w-full" value={form.type || 'factuur'} onChange={u('type')} data-testid="cockpit-type">
                    <option value="factuur">Factuur</option>
                    <option value="creditnota">Creditnota</option>
                    <option value="kassaticket">Kassaticket</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-0.5">Uitgiftedatum</label>
                  <input type="date" className="field-input w-full" value={form.uitgiftedatum || ''} onChange={u('uitgiftedatum')} data-testid="cockpit-datum" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-0.5">Vervaldatum</label>
                  <input type="date" className="field-input w-full" value={form.vervaldatum || ''} onChange={u('vervaldatum')} data-testid="cockpit-vervaldatum" />
                </div>
              </div>
            </div>

            {/* Bedragen */}
            <div className="border-t border-gray-100 pt-3">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-0.5">Excl. BTW</label>
                  <input type="number" step="0.01" className="field-input w-full text-right font-mono" value={form.bedrag_excl_btw ?? ''} onChange={u('bedrag_excl_btw')} data-testid="cockpit-excl" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-0.5">BTW %</label>
                  <select className="field-input w-full" value={form.btw_pct ?? 21} onChange={u('btw_pct')} data-testid="cockpit-btw-pct">
                    <option value={0}>0%</option>
                    <option value={6}>6%</option>
                    <option value={12}>12%</option>
                    <option value={21}>21%</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-0.5">Incl. BTW</label>
                  <input type="number" step="0.01" className="field-input w-full text-right font-mono font-bold" value={form.totaal ?? form.bedrag_incl_btw ?? ''} onChange={u('totaal')} data-testid="cockpit-totaal" />
                </div>
              </div>
            </div>

            {/* Boekhouding */}
            <div className="border-t border-gray-100 pt-3">
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-0.5">Categorie (kostenrubriek)</label>
              <select className="field-input w-full mb-2" value={form.categorie || ''} onChange={u('categorie')} data-testid="cockpit-categorie">
                <option value="">— kies —</option>
                {CATEGORIE_PLAT.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-0.5">Grootboekrek.</label>
                  <input className="field-input w-full font-mono text-xs" value={form.grootboekrekening || ''} onChange={u('grootboekrekening')} data-testid="cockpit-grootboek" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-0.5">Aftrekbaarheid %</label>
                  <select className="field-input w-full" value={form.aftrekbaarheid ?? 100} onChange={u('aftrekbaarheid')} data-testid="cockpit-aftrek">
                    <option value={0}>0%</option><option value={35}>35%</option><option value={50}>50%</option><option value={75}>75%</option><option value={100}>100%</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Nota */}
            <div className="border-t border-gray-100 pt-3">
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-0.5">Interne notitie (optioneel)</label>
              <textarea className="field-input w-full text-xs" rows={2} value={form.notitie || ''} onChange={u('notitie')} data-testid="cockpit-notitie" placeholder="bv. Verband met project X, vraag aan accountant..." />
            </div>

            {/* Save status */}
            <div className="border-t border-gray-100 pt-2 flex items-center gap-2 text-[11px]">
              {saving ? <span className="text-amber-700 inline-flex items-center gap-1"><FloppyDisk size={12} /> Opslaan...</span> :
                dirty ? <span className="text-amber-700">⚠ Niet opgeslagen wijzigingen</span> :
                <span className="text-green-700 inline-flex items-center gap-1"><CheckCircle size={12} weight="bold" /> Alles opgeslagen</span>}
              {form.totaal && <span className="ml-auto font-mono text-gray-700">€ {fmt(form.totaal)}</span>}
            </div>
          </div>

          {/* Sticky footer met Opslaan & Volgende */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-3 flex items-center gap-2">
            <button
              type="button"
              onClick={autoSave}
              disabled={saving}
              className="btn btn-secondary inline-flex items-center gap-1.5"
              data-testid="cockpit-save-btn"
              title="Enkel opslaan, blijf op deze factuur"
            >
              <FloppyDisk size={14} /> Opslaan
            </button>
            <button
              type="button"
              onClick={slaOver}
              disabled={saving}
              className="btn btn-secondary inline-flex items-center gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="cockpit-skip-btn"
              title="Sla deze factuur over (status blijft 'te verwerken') — Ctrl+Shift+Enter"
            >
              <SkipForward size={14} weight="bold" /> Sla over
              <span className="text-[10px] opacity-70 font-mono hidden lg:inline">Ctrl+⇧+↵</span>
            </button>
            <button
              type="button"
              onClick={opslaanEnVolgende}
              disabled={saving}
              className="btn btn-primary flex-1 font-bold inline-flex items-center justify-center gap-2"
              data-testid="cockpit-save-next-btn"
              title="Opslaan + goedkeuren + ga naar volgende (Ctrl+Enter)"
            >
              <Lightning size={14} weight="bold" /> Opslaan & Volgende
              <span className="text-[10px] opacity-70 font-mono hidden lg:inline">Ctrl+↵</span>
              <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
